import React, { FC, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { DragAndDrop } from '@im/ui-lib/Drag&Drop';
import { useTranslate } from '@im/react-localization';
import { formatFileSize } from 'src/util';
import UploadFileDetails from './UploadFileDetails';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export interface UploadExcelDialogProps {
  onClose: (result?: any) => void;
  uploadFileApi: any;
}

const UploadExcelDialog: FC<UploadExcelDialogProps> = ({
  onClose,
  uploadFileApi,
}) => {
  const translate = useTranslate();
  const scrollRef = useRef<HTMLDivElement | null>();
  const [isValidated, setIsValidated] = useState(false);
  const [file, setFile] = useState<File>();
  const [validation, setValidation] = useState<{ msg: string; type: string }>({
    msg: '',
    type: '',
  });

  const handleUploadDialogClose = () => {
    onClose(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleUploadFile(e.target.files);
    }
    e.target.value = '';
  };

  const handleUploadFile = (files: FileList | null) => {
    if (files) {
      if (files[0].size > MAX_FILE_SIZE) {
        setValidation({
          msg: translate('Common.ValidationMaxFileSize', {
            size: formatFileSize(MAX_FILE_SIZE),
          }),
          type: 'error',
        });
        setIsValidated(true);
      } else if (!files?.[0].name.endsWith('.xlsx')) {
        setValidation({
          msg: translate('Common.ValidationFileType', {
            type: 'Excel (.xlsx)',
          }),
          type: 'error',
        });
        setIsValidated(true);
      } else if (files?.length) {
        setFile(files[0]);
        setIsValidated(true);
        setValidation({
          msg: '',
          type: '',
        });
      }
    }
  };

  const handleResolvedFile = (res: any) => {
    if (res?.customers) {
      onClose(res);
    } else if (res?.statusCode && res.statusCode !== 200 && res.message) {
      setValidation({
        msg: res.message,
        type: 'error',
      });
      setIsValidated(true);
    } else if (res?.response || (res?.status && res.status !== 200)) {
      setValidation({
        msg:
          res?.response?.message ||
          res?.name ||
          res?.message ||
          res?.msg ||
          translate('Common.Error'),
        type: 'error',
      });
      setIsValidated(true);
    } else if (typeof res === 'string') {
      setValidation({
        msg: res,
        type: 'error',
      });
      setIsValidated(true);
    }
  };

  return (
    <Dialog onClose={handleUploadDialogClose} open={true}>
      <DialogContent ref={scrollRef} sx={{ padding: '0 !important' }}>
        <Box display={isValidated ? 'none' : 'initial'}>
          <DragAndDrop
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            deletedFiles={() => undefined}
            label={`.xlsx (max. ${
              formatFileSize(MAX_FILE_SIZE).split('.')[0]
            }MB)`}
            maxFileSize={MAX_FILE_SIZE}
            name="gds-customer-group-upload-customers"
            onClose={onClose}
            onUpload={handleUploadFile}
          />
        </Box>
        {isValidated && (
          <Stack
            direction="column"
            spacing={1.5}
            sx={(theme) => ({
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: `${theme.shape.borderRadius}px`,
              padding: `${theme.spacing(2.5)} calc(${theme.spacing(4)} * 2)`,
            })}
          >
            {file && validation.type !== 'error' && (
              <UploadFileDetails
                file={file}
                onResolved={handleResolvedFile}
                uploadFileApi={uploadFileApi}
              />
            )}
            {validation.type === 'error' && (
              <Stack
                direction="row"
                spacing={2}
                sx={(theme) => ({
                  display: 'block !important',
                  textAlign: 'center',
                })}
              >
                <Typography
                  sx={(theme) => ({
                    color: `${theme.palette.error[700]} !important`,
                    fontSize: theme.typography.body2.fontSize,
                    lineHeight: theme.typography.body2.lineHeight,
                  })}
                >
                  {validation.msg}
                </Typography>
                <Button
                  component="label"
                  sx={(theme) => ({
                    background: 'transparent !important',
                    boxShadow: theme.shadows[0],
                    margin: '0 !important',
                    width: '65px',
                  })}
                  variant="newText"
                >
                  {translate('Common.TryAgain')}
                  <input
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleFileChange}
                    type="file"
                  />
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UploadExcelDialog;
